import moment from 'moment';
import {
  isEmpty,
  isString,
  isNumber,
  isArray,
  isObject,
} from 'src/helpers/check';

export const toFloat = (value) => (isEmpty(value)
  ? 0
  : isString(value)
    ? parseFloat(value ? value.replace(/,/, '.').replace(/[^\d-.]/g, '') : '0')
    : isNumber(value)
      ? parseFloat(value)
      : 0);

export const toInt = (value) => (isEmpty(value) ? 0 : isNumber(parseInt(value)) ? parseInt(value) : 0);

export const toInt10 = (value) => (isEmpty(value) ? 0 : isNumber(parseInt(value, 10)) ? parseInt(value, 10) : 0);

export const toBool = (value) => (isEmpty(value) ? false : !!value);

export const toString = (value) => (isEmpty(value) ? '' : value.toString());

export const toArray = (value) => (isEmpty(value) ? [] : isArray(value) ? value : [value]);

export const jsonToObject = (value) => {
  if (isEmpty(value)) return {};

  if (isString(value)) {
    try {
      const parsed = JSON.parse(value);
      return isObject(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return isObject(value) ? value : {};
};

export const phoneToString = (value) => (isEmpty(value) ? '' : `${value}`.replace(/\D/g, ''));

export const phoneToInt = (value) => toInt10(phoneToString(value));

export const phoneToIntWithNull = (value) => (isEmpty(value) || !value ? null : toInt10(phoneToString(value)));

export const toPhoneNumber = (value) => (isEmpty(value) || value.length !== 10 ? value : value.replace(/^(?<s1>\d{3})(?<s2>\d{3})(?<s3>\d{4})$/, '($<s1>) $<s2>-$<s3>'));

export const phoneWithoutPlusOne = (phone) => (isEmpty(phone) ? '' : toPhoneNumber(phone.toString().replace(/^\+1/, '')));

export const toLowerCase = (value) => (isEmpty(value) ? '' : value.toLowerCase());

export const toUpperCase = (value) => (isEmpty(value) ? '' : value.toUpperCase());

export const arrayToString = (list, separator = ', ') => toArray(list).join(separator);

export const arrayToStringWithoutEmpty = (list, separator = ', ') => toArray(list).filter((item) => !isEmpty(item)).join(separator);

export const arrayStringToObjectLabelValue = (list) => toArray(list).map((item) => (!item?.label ? {
  label: item,
  value: item,
} : item));

export const stringFromObjectLabelValue = (value) => value?.value || value;

export const stringFromObjectLabelLabel = (value) => value?.label || value;

export const arrayStringFromObjectLabelValue = (list) => toArray(list).map(stringFromObjectLabelValue);

export const arrayStringFromObjectLabelLabel = (list) => toArray(list).map(stringFromObjectLabelLabel);

export const stringToNull = (value) => (value === '' ? null : value);

export const stringToArrayWithoutEmpty = (value, separator = ',') => toArray(toString(value).split(separator)).filter((item) => item !== '');

export const intToNull = (value) => (value === '' ? null : toInt10(value));

export const toFormInitValue = (value) => (isEmpty(value) && value !== 0 ? '' : value);

export const numberToCurrency = (value) => (toFloat(value).toFixed(2));

export const numberToCurrencyAndInt = (value) => (toFloat(value).toFixed(2).replace(/\.00$/gui, ''));

export const toDayDifferent = (timestamp1, timestamp2) => moment(timestamp1).utc().diff(moment(timestamp2).utc(), 'days');

export const toSortFieldObj = (sortValues) => {
  const valuesObj = {};
  toArray(sortValues).forEach((item) => {
    if (item.length === 2) {
      const [item0, item1] = item || [];
      if (item0 && item1) {
        valuesObj[item0] = item1;
      }
    } else if (item.length === 3) {
      const [item0, , item2] = item || [];
      if (item0 && item2) {
        valuesObj[item0] = item2;
      }
    }
  });
  if (isEmpty(valuesObj)) {
    return null;
  }

  return valuesObj;
};

export const toInitValueForCategory = (initValue) => (initValue ? {
  label: initValue,
  value: initValue,
} : '');

export const toMinDate = (date) => moment(date).set({
  hour: 0,
  minute: 0,
  second: 0,
  millisecond: 0,
});

export const toMaxDate = (date) => moment(date).set({
  hour: 23,
  minute: 59,
  second: 59,
  millisecond: 999,
});

export const numberToString000 = (value) => `000${toString(value).replace(/\D/gui, '')}`.slice(-3);

export const toMessageCode = (code1, code2) => `${code1}${numberToString000(code2)}`;

export const toDateTime = (date) => moment(date).format('MMM DD YYYY | hh:mm a');

export const formatDateTimeDayMDDYYHMMpm = (date) => moment(date).format('ddd M/D/YY h:mm a'); // e.g., Mon 4/15/25 3:14 pm

export const formatDateTimeMMMDYYYYHMMpm = (date) => moment(date).format('MMM D, YYYY h:mm a'); // e.g., Apr 15, 2025 3:14 pm

export const formatDateMDYYHMMpm = (date) => moment(date).format('M/D/YY h:mma'); // e.g., 4/18/25 11:58am

// Relative "x ago" using moment's fromNow() — matches how CP shows
// timestamps elsewhere (ProjectCard, PostCard, Contacts). Returns null
// for empty/invalid input so callers can render an explicit empty state.
export const relativeTimeFromNow = (date) => {
  if (!date) return null;
  const m = moment(date);
  return m.isValid() ? m.fromNow() : null; // e.g., "43 minutes ago", "2 hours ago", "3 days ago"
};

export const formatBirthdayMDYSeparateFieldsWithDashes = (month, day, year) => {
  // for each piece, if it's null/undefined/empty, show "--"
  const mm = (month != null && month !== '')
    ? String(month).padStart(2, '0')
    : '--';
  const dd = (day != null && day !== '')
    ? String(day).padStart(2, '0')
    : '--';
  const yy = (year != null && year !== '')
    ? String(year)
    : '--';

  return `${mm}/${dd}/${yy}`;
};

// 2025-07-23 - This is also on the back end.
const csvStringToArrayWithoutEmptyRowsLeaveLineBreaks = (csvContent) => {
  const rows = [];
  let currentRow = '';
  let insideQuotes = false;

  for (let i = 0; i < csvContent.length; i++) {
    const char = csvContent[i];
    const nextChar = csvContent[i + 1];

    if (char === '"') {
      insideQuotes = !insideQuotes;
    }

    if ((char === '\n' || (char === '\r' && nextChar === '\n')) && !insideQuotes) {
      rows.push(currentRow.trim());
      currentRow = '';
      if (char === '\r' && nextChar === '\n') {
        i++; // skip \n after \r
      }
    } else {
      currentRow += char;
    }
  }

  if (currentRow.trim() !== '') {
    rows.push(currentRow.trim());
  }

  return rows.filter((row) => row !== '');
};

export const csvStringToArrayNoEmptyRowsBetween = (csvContent) => {
  // Strip BOM character that some editors (Excel, Numbers) prepend
  const cleaned = (csvContent || '').replace(/^\uFEFF/, '');

  // step 1: split into raw lines (preserves quoted newlines)
  const rawRows = csvStringToArrayWithoutEmptyRowsLeaveLineBreaks(cleaned);

  // step 2: drop any row where every comma-separated cell is blank
  // Also strips surrounding quotes from each cell before checking
  return rawRows.filter((row) => {
    const cells = row.split(',');
    return cells.some((cell) => {
      const trimmed = cell.trim().replace(/^"(.*)"$/, '$1').trim();
      return trimmed !== '';
    });
  });
};

export const toFullName = (firstName, lastName) => arrayToStringWithoutEmpty(
  [
    firstName,
    lastName,
  ],
  ' '
);

export const toSMSSendingLimits = (rateLimits) => {
  const {
    dailyTextHardLimit,
    dailyTextsSent,
    waitTime,
    serverTime,
  } = rateLimits || {};

  const waitTimeNumber = toInt10(waitTime);
  const serverTimeWithWait = waitTimeNumber > 0 ? moment(serverTime).utc().add(waitTimeNumber, 'milliseconds') : moment().utc();

  const isDailySMSSendingDisabled = !!(toInt10(dailyTextHardLimit) <= toInt10(dailyTextsSent));

  return {
    isDailySMSSendingDisabled,
    serverTimeWithWait,
  };
};

export const toContactLabelValue = (option) => {
  if (option?.phone1) {
    return `${option?.firstName ? `${option.firstName} ` : ''}${option?.lastName ? `${option.lastName} ` : ''}${toPhoneNumber(option.phone1)}`;
  }
  if (option?.email) {
    return `${option?.firstName ? `${option.firstName} ` : ''}${option?.lastName ? `${option.lastName} ` : ''}${option.email}`;
  }

  return `${option?.firstName ? `${option.firstName} ` : ''}${option?.lastName ? `${option.lastName} ` : ''}`;
};

export const toPhoneTextingInfo = ({
  phone,
  doNotText,
  isOptedInToTexting,
}) => {
  const newPhone = phoneToIntWithNull(phone);

  if (isEmpty(newPhone)) {
    return {};
  }

  return {
    [newPhone]: {
      ...doNotText !== undefined ? {
        doNotText: !!doNotText,
      } : {},
      ...isOptedInToTexting !== undefined ? {
        isOptedInToTexting: !!isOptedInToTexting,
      } : {},
    },
  };
};

export const toUTCFromLocalTimeOfDay = (timeOfDay) => {
  const newTimeOfDay = (moment(timeOfDay, 'HH:mm').isValid()) ? moment(timeOfDay, 'HH:mm') : null;

  return newTimeOfDay ? newTimeOfDay.utc().format('HH:mm') : null;
};

export const toLocalFromUTCTimeOfDay = (timeOfDay) => {
  const newTimeOfDay = (moment(timeOfDay, 'HH:mm').isValid()) ? moment.utc(timeOfDay, 'HH:mm') : null;

  return newTimeOfDay ? newTimeOfDay.local().format('HH:mm') : null;
};
